/**
 * Button Component
 * Reusable button component with multiple variants and sizes
 */

import { forwardRef, useState, useRef, useEffect } from 'react';
import styles from './Button.module.css';

export interface DropdownOption {
  label: React.ReactNode;
  value: string;
  onClick: () => void;
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'filled' | 'outline' | 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  dropdownOptions?: DropdownOption[];
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
    ({ 
        variant = 'primary', 
        size = 'md', 
        fullWidth = false, 
        loading = false, 
        className = '', 
        disabled, 
        children,
        dropdownOptions,
        onClick,
        ...props 
    }, ref) => {
        const [isDropdownOpen, setIsDropdownOpen] = useState(false);
        const dropdownRef = useRef<HTMLDivElement>(null);
        const hasDropdown = dropdownOptions && dropdownOptions.length > 0;

        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                    setIsDropdownOpen(false);
                }
            };

            if (isDropdownOpen) {
                document.addEventListener('mousedown', handleClickOutside);
            }

            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [isDropdownOpen]);

        const buttonClasses = [
            styles.button,
            styles[`button__${variant}`],
            styles[`button__${size}`],
            fullWidth && styles.button__fullWidth,
            loading && styles.button__loading,
            hasDropdown && styles.button__withDropdown,
            className
        ].filter(Boolean).join(' ');

        const handleMainClick = (e: React.MouseEvent<HTMLButtonElement>) => {
            if (onClick) {
                onClick(e);
            }
        };

        const handleDropdownToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation();
            if (!disabled && !loading) {
                setIsDropdownOpen(!isDropdownOpen);
            }
        };

        const handleOptionClick = (option: DropdownOption) => {
            setIsDropdownOpen(false);
            option.onClick();
        };

        if (hasDropdown) {
            return (
                <div className={styles.splitButtonContainer} ref={dropdownRef}>
                    <div className={styles.splitButtonGroup}>
                        <button
                            ref={ref}
                            className={`${buttonClasses} ${styles.splitButton__main}`}
                            disabled={disabled || loading}
                            onClick={handleMainClick}
                            {...props}
                        >
                            {loading && <span className={styles.spinner} />}
                            <span className={styles.content}>{children}</span>
                        </button>
                        <button
                            className={`${buttonClasses} ${styles.splitButton__dropdown}`}
                            disabled={disabled || loading}
                            onClick={handleDropdownToggle}
                            type="button"
                        >
                            <svg 
                                className={styles.chevron} 
                                width="12" 
                                height="12" 
                                viewBox="0 0 12 12" 
                                fill="none"
                            >
                                <path 
                                    d="M2.5 4.5L6 8L9.5 4.5" 
                                    stroke="currentColor" 
                                    strokeWidth="1.5" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                    {isDropdownOpen && (
                        <div className={styles.dropdownMenu}>
                            {dropdownOptions.map((option) => (
                                <button
                                    key={option.value}
                                    className={styles.dropdownOption}
                                    onClick={() => handleOptionClick(option)}
                                    type="button"
                                >
                                    {option.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            );
        }

        return (
            <button
                ref={ref}
                className={buttonClasses}
                disabled={disabled || loading}
                onClick={onClick}
                {...props}
            >
                {loading && <span className={styles.spinner} />}
                <span className={styles.content}>{children}</span>
            </button>
        );
    }
);

Button.displayName = 'Button';

export default Button;
